import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import DialogHeader from 'components/dialogs/DialogHeader';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';
import { editMedicalOpinion } from 'api/analytics';
import forge from 'node-forge';
import { useDispatch } from 'react-redux';
import { showSuccessToast, showErrorToast } from 'features/toastSlice';
import styles from './styles.module.css';

function EditMedicalOpinionDialog({ open, onClose, medicalOpinion, publicKey, patientName, editorName }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [opinionText, setOpinionText] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const MAX_CHARACTERS = 240;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (medicalOpinion) {
      setOpinionText(medicalOpinion.text || '');
      setCharacterCount(medicalOpinion.text?.length || 0);
    }
  }, [medicalOpinion]);

  const encryptData = (data) => {
    const rsa = forge.pki.publicKeyFromPem(publicKey);
    const utf8Bytes = forge.util.encodeUtf8(data);
    const encrypted = rsa.encrypt(utf8Bytes, 'RAW');
    return forge.util.encode64(encrypted);
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARACTERS) {
      setOpinionText(text);
      setCharacterCount(text.length);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);

    const encryptedData = {
      acquisition_id: medicalOpinion.acquisition_id,
      editor_name: editorName,
      new_medical_opinion: encryptData(opinionText),
    };

    try {
      const response = await editMedicalOpinion(encryptedData);

      if (response.data && !response.data.error) {
        dispatch(showSuccessToast(t('physio.physio-edit-medical-opinion-dialog.save-success')));
        onClose(true);
      } else {
        const errorMessage = response.data?.message || t('physio.physio-edit-medical-opinion-dialog.save-error');
        dispatch(showErrorToast(errorMessage));
      }
    } catch (error) {
      console.error('Error saving medical opinion:', error);
      dispatch(showErrorToast(t('physio.physio-edit-medical-opinion-dialog.save-error')));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : () => onClose(false)} maxWidth="md" fullWidth>
      <DialogHeader>{t('physio.physio-edit-medical-opinion-dialog.edit-medical-opinion')}</DialogHeader>
      <DialogHeader variant="small">
        <strong>UMI:</strong> {medicalOpinion?.acquisition_id}
      </DialogHeader>
      {patientName && (
        <DialogHeader variant="small">
          <strong>{t('physio.physio-edit-medical-opinion-dialog.patient')}:</strong> {patientName}
        </DialogHeader>
      )}
      <DialogContent className={styles.DialogContent}>
        <TextField
          fullWidth
          multiline
          rows={5}
          margin="normal"
          label={t('physio.physio-edit-medical-opinion-dialog.medical-opinion')}
          value={opinionText}
          onChange={handleTextChange}
          inputProps={{
            maxLength: MAX_CHARACTERS,
            disabled: isLoading,
          }}
          disabled={isLoading}
        />
        <Typography
          variant="caption"
          color={characterCount === MAX_CHARACTERS ? 'error' : 'textSecondary'}
          sx={{ display: 'block', textAlign: 'right', mt: 1 }}
        >
          {characterCount}/{MAX_CHARACTERS} {t('physio.physio-edit-medical-opinion-dialog.characters')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => onClose(false)} disabled={isLoading}>
          {t('physio.physio-edit-medical-opinion-dialog.button-cancel')}
        </Button>
        <Button sx={{ margin: '16px 0' }} color="gradient" onClick={handleSave} disabled={isLoading}>
          {isLoading
            ? t('physio.physio-edit-medical-opinion-dialog.button-saving')
            : t('physio.physio-edit-medical-opinion-dialog.button-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditMedicalOpinionDialog;
